// src/Pages/Commerce/CommerceTransactions.js
//
// Blitz Commerce — Transactions
//
// Independent page (routes.commerceTransactions = "/commerce/transactions")
// showing every checkout-driven payment as a money-movement record: what
// the buyer paid, which rail it came through, whether it's currently held
// in the merchant's reserve window, and what actually lands in Blitz
// Balance. None of that is visible today — ProductManagement's
// "Purchasers" tab is CRM-flavored (who bought), not ledger-flavored (how
// the money moved).
//
// Data source is the *existing* client.commerce.fetchPurchasers() call
// (GET /commerce/purchasers per docs/cummerce/backend-t2a.md — the order
// list), joined client-side with fetchReturns() so a refunded/disputed
// order shows its return status inline. No new backend endpoint required.
//
// Fields not yet in the documented purchasers response — payment_provider,
// external_transaction_id, reserve_held, reserve_release_at, net_amount —
// are read defensively (optional chaining, graceful fallback) so this page
// renders correctly today and picks up the fuller picture automatically
// once the backend adds them. See the bottom of this file for the exact
// shape.

import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Tooltip,
  Skeleton,
  Alert,
  Grid,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Stack,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import LockClockOutlinedIcon from "@mui/icons-material/LockClockOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import client from "../../API";
import useAuth from "../../Hooks/use-auth";
import { useNavigate } from "react-router-dom";
import routes from "../../Config/routes.js";
import { COMMERCE_BRAND, formatCurrency, formatDate, StatCard, EmptyState } from "./commerceShared.js";

/*------------------------------------------------------------------
  Provider / status metadata
-------------------------------------------------------------------*/
const PROVIDER_META = {
  stripe: { label: "Card · Stripe", color: "#635bff", icon: <CreditCardOutlinedIcon fontSize="inherit" /> },
  paypal: { label: "PayPal", color: "#003087", icon: <AccountBalanceOutlinedIcon fontSize="inherit" /> },
  balance: { label: "Blitz Balance", color: COMMERCE_BRAND.sky, icon: <AccountBalanceWalletOutlinedIcon fontSize="inherit" /> },
};
const getProviderMeta = (provider) =>
  PROVIDER_META[(provider || "").toLowerCase()] || {
    label: "Unknown rail",
    color: "#64748b",
    icon: <ReceiptLongOutlinedIcon fontSize="inherit" />,
  };

const STATUS_COLOR = {
  fulfilled: "success",
  paid: "success",
  pending: "warning",
  refunded: "error",
  disputed: "error",
};

const ProviderChip = ({ provider }) => {
  const meta = getProviderMeta(provider);
  return (
    <Chip
      size="small"
      icon={React.cloneElement(meta.icon, { style: { color: meta.color } })}
      label={meta.label}
      variant="outlined"
      sx={{
        borderColor: alpha(meta.color, 0.4),
        color: meta.color,
        fontWeight: 600,
        "& .MuiChip-icon": { fontSize: 14 },
      }}
    />
  );
};

/*------------------------------------------------------------------
  Transaction details dialog — the money-flow timeline
-------------------------------------------------------------------*/
function TransactionDetailsDialog({ open, onClose, transaction, matchedReturn }) {
  const theme = useTheme();
  if (!transaction) return null;

  const gross = parseFloat(transaction.amount || 0);
  const reserveHeld =
    transaction.reserve_held !== undefined && transaction.reserve_held !== null
      ? parseFloat(transaction.reserve_held)
      : null;
  const net =
    transaction.net_amount !== undefined && transaction.net_amount !== null
      ? parseFloat(transaction.net_amount)
      : reserveHeld !== null
      ? gross - reserveHeld
      : null;
  const isRefunded = transaction.status === "refunded";

  const steps = [
    {
      label: "Buyer paid",
      detail: `${formatCurrency(gross)} via ${getProviderMeta(transaction.payment_provider).label}`,
      color: theme.palette.text.primary,
    },
  ];
  if (reserveHeld !== null && reserveHeld > 0) {
    steps.push({
      label: "Held in reserve",
      detail: `${formatCurrency(reserveHeld)} withheld${
        transaction.reserve_release_at
          ? ` · releases ${formatDate(transaction.reserve_release_at)}`
          : " · release date not yet available"
      }`,
      color: theme.palette.warning.main,
    });
  }
  steps.push({
    label: isRefunded ? "Refunded to buyer" : "Credited to Blitz Balance",
    detail: isRefunded
      ? `${formatCurrency(net ?? gross)} reversed`
      : `${formatCurrency(net ?? gross)} landed in your Blitz Balance`,
    color: isRefunded ? theme.palette.error.main : theme.palette.success.main,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {transaction.product_name || "Order"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Order #{transaction.order_id}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">Buyer</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {transaction.customer_name || "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {transaction.customer_email || "—"}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Money flow
            </Typography>
            <Stack spacing={1.5}>
              {steps.map((step, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: step.color,
                      mt: 0.6,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {step.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {step.detail}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>

          {matchedReturn && (
            <>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                  Linked return / dispute
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Chip
                    size="small"
                    label={matchedReturn.status}
                    color={matchedReturn.status === "approved" ? "error" : matchedReturn.status === "denied" ? "success" : "warning"}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Filed {formatDate(matchedReturn.filed_at)}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {matchedReturn.reason || "No reason provided."}
                </Typography>
              </Box>
            </>
          )}

          <Divider />

          <Box>
            <Typography variant="caption" color="text.secondary">Idempotency / transaction id</Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", fontSize: "0.8rem", wordBreak: "break-all" }}
              >
                {transaction.external_transaction_id || "Not yet reported by backend"}
              </Typography>
              {transaction.external_transaction_id && (
                <Tooltip title="Copy">
                  <IconButton
                    size="small"
                    onClick={() => navigator.clipboard.writeText(transaction.external_transaction_id)}
                  >
                    <ContentCopyOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

/*------------------------------------------------------------------
  MAIN PAGE
-------------------------------------------------------------------*/
export default function CommerceTransactions() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [providerFilter, setProviderFilter] = useState("all");
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const { data: companyData, isLoading: isLoadingCompany } = useQuery(
    ["company", userInfo?.company_id],
    () => client.companies.listFetch({ id: userInfo?.company_id }),
    { enabled: !!userInfo?.company_id, staleTime: 1000 * 60 * 30 },
  );
  const company =
    (Array.isArray(companyData) ? companyData[0] : null) || companyData?.data || companyData || {};
  const isAlphaFreeAccount = company.account_status === "AlphaFree";

  const {
    data: orders,
    isLoading: isLoadingOrders,
    isFetching: isFetchingOrders,
    refetch,
  } = useQuery(
    ["commerce", "purchasers"],
    () => client.commerce.fetchPurchasers(),
    { enabled: isAlphaFreeAccount, staleTime: 1000 * 30 },
  );

  const { data: returns } = useQuery(
    ["commerce", "returns"],
    () => client.commerce.fetchReturns(),
    { enabled: isAlphaFreeAccount, staleTime: 1000 * 30 },
  );

  const returnsByOrderId = useMemo(() => {
    const map = new Map();
    (returns || []).forEach((r) => map.set(r.order_id, r));
    return map;
  }, [returns]);

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (orders || []).filter((o) => {
      const matchesSearch =
        !term ||
        (o.customer_name || "").toLowerCase().includes(term) ||
        (o.customer_email || "").toLowerCase().includes(term) ||
        (o.product_name || "").toLowerCase().includes(term) ||
        String(o.order_id || "").includes(term) ||
        (o.external_transaction_id || "").toLowerCase().includes(term);
      const matchesStatus = statusFilter === "all" || o.status === statusFilter;
      const matchesProvider =
        providerFilter === "all" || (o.payment_provider || "").toLowerCase() === providerFilter;
      return matchesSearch && matchesStatus && matchesProvider;
    });
  }, [orders, search, statusFilter, providerFilter]);

  const summary = useMemo(() => {
    const list = orders || [];
    const gross = list.reduce((sum, o) => sum + parseFloat(o.amount || 0), 0);
    const reserveHeld = list.reduce((sum, o) => sum + parseFloat(o.reserve_held || 0), 0);
    const refunded = list
      .filter((o) => o.status === "refunded")
      .reduce((sum, o) => sum + parseFloat(o.amount || 0), 0);
    const net = list.reduce((sum, o) => {
      const n = o.net_amount !== undefined && o.net_amount !== null ? parseFloat(o.net_amount) : parseFloat(o.amount || 0) - parseFloat(o.reserve_held || 0);
      return sum + n;
    }, 0);
    return { gross, reserveHeld, refunded, net, count: list.length };
  }, [orders]);

  if (!isLoadingCompany && !isAlphaFreeAccount) {
    return (
      <Box sx={{ maxWidth: 640, mx: "auto", mt: 6, px: 2 }}>
        <Alert severity="info" variant="outlined">
          Blitz Commerce is currently in Alpha and only available to select
          accounts. Reach out to your account manager if you'd like early
          access.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* ── Header ─────────────────────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${COMMERCE_BRAND.skyDeep} 0%, ${COMMERCE_BRAND.sky} 100%)`,
          color: "#fff",
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 4 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <IconButton
            size="small"
            onClick={() => navigate(routes.commerce)}
            sx={{ color: "#fff", bgcolor: alpha("#fff", 0.15), "&:hover": { bgcolor: alpha("#fff", 0.25) } }}
          >
            <ArrowBackOutlinedIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600 }}>
            Blitz Commerce
          </Typography>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "flex-end" }} spacing={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
              Transactions
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 520 }}>
              Every payment that comes through your checkout links — from what
              the buyer paid to what lands in your Blitz Balance.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => refetch()}
            disabled={isFetchingOrders}
            sx={{
              textTransform: "none",
              color: "#fff",
              borderColor: alpha("#fff", 0.5),
              "&:hover": { borderColor: "#fff", bgcolor: alpha("#fff", 0.1) },
            }}
          >
            {isFetchingOrders ? "Refreshing…" : "Refresh"}
          </Button>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        {/* ── Summary cards ────────────────────────────────────── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<TrendingUpOutlinedIcon fontSize="small" />}
              label="Gross volume"
              value={formatCurrency(summary.gross)}
              sublabel={`${summary.count} transaction${summary.count !== 1 ? "s" : ""}`}
              loading={isLoadingOrders}
              accent="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<LockClockOutlinedIcon fontSize="small" />}
              label="Currently held in reserve"
              value={formatCurrency(summary.reserveHeld)}
              sublabel="Releases automatically per your reserve window"
              loading={isLoadingOrders}
              accent="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
              label="Net to Blitz Balance"
              value={formatCurrency(summary.net)}
              sublabel="After reserve withholding"
              loading={isLoadingOrders}
              accent="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AssignmentReturnOutlinedIcon fontSize="small" />}
              label="Refunded"
              value={formatCurrency(summary.refunded)}
              loading={isLoadingOrders}
              accent="error"
            />
          </Grid>
        </Grid>

        <Alert
          severity="info"
          variant="outlined"
          icon={<InfoOutlinedIcon fontSize="small" />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          Orders made in the first days after your first product link don't
          land in your balance immediately — a percentage is held back in
          reserve, then released automatically. That's reflected per-order
          below.
        </Alert>

        {/* ── Filters ──────────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField
              size="small"
              placeholder="Search buyer, product, order id, transaction id…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="fulfilled">Fulfilled</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Payment rail</InputLabel>
              <Select value={providerFilter} label="Payment rail" onChange={(e) => setProviderFilter(e.target.value)}>
                <MenuItem value="all">All rails</MenuItem>
                <MenuItem value="stripe">Card · Stripe</MenuItem>
                <MenuItem value="paypal">PayPal</MenuItem>
                <MenuItem value="balance">Blitz Balance</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {/* ── Table ────────────────────────────────────────────── */}
        {isLoadingOrders ? (
          <Skeleton variant="rounded" height={320} />
        ) : filteredOrders.length ? (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Transaction</TableCell>
                  <TableCell>Buyer</TableCell>
                  <TableCell>Rail</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell align="right">Reserve held</TableCell>
                  <TableCell align="right">Net</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.map((o) => {
                  const reserveHeld =
                    o.reserve_held !== undefined && o.reserve_held !== null ? parseFloat(o.reserve_held) : null;
                  const net =
                    o.net_amount !== undefined && o.net_amount !== null
                      ? parseFloat(o.net_amount)
                      : reserveHeld !== null
                      ? parseFloat(o.amount || 0) - reserveHeld
                      : null;
                  return (
                    <TableRow
                      key={o.order_id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => setSelectedTransaction(o)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {o.product_name || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{o.order_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{o.customer_name || "—"}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {o.customer_email || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ProviderChip provider={o.payment_provider} />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(o.amount)}</TableCell>
                      <TableCell align="right">
                        {reserveHeld !== null && reserveHeld > 0 ? (
                          <Tooltip title="Withheld under your reserve window">
                            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                              {formatCurrency(reserveHeld)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {net !== null ? formatCurrency(net) : formatCurrency(o.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={o.status}
                          color={STATUS_COLOR[o.status] || "default"}
                          sx={{ textTransform: "capitalize" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(o.purchased_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <EmptyState
              icon={<ReceiptLongOutlinedIcon fontSize="inherit" />}
              title={orders?.length ? "No transactions match your filters" : "No transactions yet"}
              description={
                orders?.length
                  ? "Try clearing search or filters to see everything."
                  : "Once a buyer completes checkout on one of your product links, the full payment breakdown will show up here."
              }
            />
          </Paper>
        )}
      </Box>

      <TransactionDetailsDialog
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        transaction={selectedTransaction}
        matchedReturn={selectedTransaction ? returnsByOrderId.get(selectedTransaction.order_id) : null}
      />
    </Box>
  );
}

/*------------------------------------------------------------------
  ADDITIVE FIELDS THIS PAGE READS OPTIMISTICALLY
-------------------------------------------------------------------
  All of these are read defensively (?. / undefined fallback) so the page
  works today against the documented client.commerce.fetchPurchasers()
  shape. Once the backend adds them to that response, this page picks
  them up automatically:

  client.commerce.fetchPurchasers()
    -> [{
         ...existing fields (order_id, client_id, customer_name,
             customer_email, product_name, amount, purchased_at, status),
         payment_provider,        // "stripe" | "paypal" | "balance"
         external_transaction_id, // idempotency key from orders/complete
         reserve_held,            // amount withheld this order, if any
         reserve_release_at,      // ISO date reserve unlocks
         net_amount,              // amount - reserve_held, once known
       }]
--------------------------------------------------------------------*/
